/* osyn-home-stage.js — Section manette on-scroll (Osyn) -----------------------
 * <osyn-home-stage> : séquence WebP rendue sur <canvas> dans un stage collant,
 * scrub amorti au scroll (lerp exponentiel temporel). Pilote le rail OSYN et les
 * containers (callouts) du design system selon la progression.
 *
 * Fidèle à l'asset : les frames sont OPAQUES sur fond clair (~#F8F8F8) → canvas
 * opaque + fill de la même couleur (raccord sans couture, façon Apple/Satisfy).
 * Mémoire BORNÉE : frames en <img> évictables + fenêtre de décodage glissante
 * (jamais d'ImageBitmap épinglé → pas d'OOM à 300+ frames sur mobile).
 * Couplage Concept limité aux contrats stables (mesure JS du header, events).
 * -------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.customElements && customElements.get('osyn-home-stage')) return;

  var MQ_DESKTOP = '(min-width: 1024px)';
  var MQ_REDUCED = '(prefers-reduced-motion: reduce)';
  var AHEAD = 10; // frames décodées en avance (lissage scrub)

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

  class OsynHomeStage extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.section      = this.closest('.osyn-home-stage') || this;
      this.view         = this.querySelector('[data-osyn-stage-view]');
      this.canvas       = this.querySelector('canvas');
      this.poster       = this.querySelector('[data-osyn-stage-poster]');
      this.railThumb    = this.querySelector('[data-osyn-rail-thumb]');
      this.railSteps    = [].slice.call(this.querySelectorAll('[data-osyn-rail] li'));
      this.calloutsWrap = this.querySelector('[data-osyn-callouts]');
      this.callouts     = [].slice.call(this.querySelectorAll('[data-osyn-callout]'));
      if (!this.canvas || !this.view) return;

      var d = this.dataset;
      this.assetBase    = d.assetBase || '';
      this.framePrefix  = d.framePrefix || 'osyn-stage-';
      this.frameExt     = d.frameExt || 'webp';
      this.frameCount   = parseInt(d.frameCount, 10) || 1;
      this.frameDigits  = parseInt(d.frameDigits, 10) || 3;
      this.widthDesktop = parseInt(d.widthDesktop, 10) || 1536;
      this.widthMobile  = parseInt(d.widthMobile, 10) || 1024;
      this.smoothingMs  = parseInt(d.smoothing, 10) || 300;
      this.endProgress  = (parseFloat(d.end) || 100) / 100;
      this.bg           = d.bg || '#F8F8F8';
      this.mobileStride = Math.max(1, parseInt(d.mobileStride, 10) || 2);
      this.assetsVersion = d.assetsVersion || '1';

      this.isDesktop = matchMedia(MQ_DESKTOP).matches;
      this.reduced   = matchMedia(MQ_REDUCED).matches;
      this.saveData  = !!(navigator.connection && navigator.connection.saveData);

      this.ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';

      this.frames   = new Array(this.frameCount); // HTMLImageElement (évictables)
      this.p = 0; this.target = 0; this.raf = null;
      this._lastTs = 0; this.drawnIdx = -1;

      // --- Mode vidéo mobile (pattern Loop) : décodage MATÉRIEL hors main-thread ---
      // Sur mobile, si une <video> est fournie, elle REMPLACE le scrub canvas. Court-circuit
      // AVANT tout pipeline canvas → 0 frame WebP téléchargée. Desktop (isDesktop) n'entre jamais
      // dans cette branche : son chemin scrub reste structurellement identique.
      this.video     = this.querySelector('[data-osyn-stage-video]');
      this.videoRate = clamp(parseFloat(d.videoRate) || 1, 1, 4);
      // Le mode (vidéo mobile ↔ scrub desktop) est fixé au boot selon la largeur. Un franchissement
      // du point de rupture (rotation d'une tablette, resize desktop) doit repasser dans le bon mode :
      // les 2 pipelines diffèrent trop pour un swap à chaud → re-boot par reload (hors éditeur).
      if (this.video) {
        this._modeMq = matchMedia(MQ_DESKTOP);
        this._onModeCross = function () { if (!(window.Shopify && window.Shopify.designMode)) location.reload(); };
        if (this._modeMq.addEventListener) this._modeMq.addEventListener('change', this._onModeCross);
        else if (this._modeMq.addListener) this._modeMq.addListener(this._onModeCross);
      }
      // Desktop : la <video> porte autoplay + <source> dans le HTML (recette Loop pour iOS) mais ne sert
      // pas ici (le desktop garde le scrub) → on la neutralise pour ne RIEN télécharger (retire la source
      // + autoplay, annule le load entamé au parse ; script defer → au pire quelques Ko avant l'abort).
      if (this.video && this.isDesktop) {
        this.video.removeAttribute('autoplay');
        while (this.video.firstChild) this.video.removeChild(this.video.firstChild);
        this.video.removeAttribute('src');
        try { this.video.load(); } catch (e) {}
      }
      if (this.video && !this.isDesktop) {
        this._measureHeader();      // offset header → intro sticky correcte
        this._layoutCallouts();     // callouts dans la vue (pile sous la vidéo)
        this.introEl    = this.section.querySelector('[data-osyn-intro]');
        this.introInner = this.section.querySelector('.osyn-home-stage__intro-inner');
        this._paneH     = this.introInner ? this.introInner.getBoundingClientRect().height : window.innerHeight;
        // callouts + rail rendus STATIQUEMENT (aucun vide sous la vidéo qui loop)
        this.callouts.forEach(function (c) { c.classList.add('is-visible'); });
        this.railSteps.forEach(function (r) { r.classList.add('is-on'); });
        // autoplay muet inline pour TOUS (parti pris « comme Loop Earplugs » : l'attribut autoplay est
        // en dur dans le HTML) → on branche toujours vitesse + IO play/pause (pas de court-circuit RM :
        // sinon la vidéo jouerait à 1× sans pause hors-écran ; la retenue RM se fait ailleurs).
        this._initVideoMode();
        if (!this._introHidden()) this._setupIntroReveal();   // intro affichée sur mobile : animation d'entrée
        return;
      }

      this._sizeCanvas();
      this._measureHeader();
      this._layoutCallouts();

      // intro plein écran (titre) : refs + hauteur réelle du pane collant (svh − header, posée après _measureHeader)
      this.introEl    = this.section.querySelector('[data-osyn-intro]');
      this.introInner = this.section.querySelector('.osyn-home-stage__intro-inner');
      this._paneH     = this.introInner ? this.introInner.getBoundingClientRect().height : window.innerHeight;

      // reduced-motion / save-data : statique (poster hero), callouts + rail figés visibles
      if (this.reduced || (this.saveData && !this.isDesktop)) {
        this.callouts.forEach(function (c) { c.classList.add('is-visible'); });
        this.railSteps.forEach(function (r) { r.classList.add('is-on'); });
        return;
      }

      this._armWarm();
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._tick = this._tick.bind(this);
      addEventListener('scroll', this._onScroll, { passive: true });
      addEventListener('resize', this._onResize);
      this._onScroll();
      this._setupIntroReveal();
    }

    disconnectedCallback() {
      if (this._onScroll) removeEventListener('scroll', this._onScroll);
      if (this._onResize) removeEventListener('resize', this._onResize);
      if (this._onIntroScroll) removeEventListener('scroll', this._onIntroScroll);
      if (this._onIntroResize) removeEventListener('resize', this._onIntroResize);
      if (this._modeMq && this._onModeCross) {
        if (this._modeMq.removeEventListener) this._modeMq.removeEventListener('change', this._onModeCross);
        else if (this._modeMq.removeListener) this._modeMq.removeListener(this._onModeCross);
      }
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this._introRaf) cancelAnimationFrame(this._introRaf);
      if (this._revealIO) this._revealIO.disconnect();
      if (this._introIO) this._introIO.disconnect();
      if (this._videoIO) this._videoIO.disconnect();
      clearTimeout(this._watch);
    }

    _frameWidth() { return this.isDesktop ? this.widthDesktop : this.widthMobile; }

    frameUrl(i) {
      var num = String(i + 1).padStart(this.frameDigits, '0');
      // ?v= = cache-bust quand le contenu des frames change (noms stables) ; &width= = hint CDN
      return this.assetBase + this.framePrefix + num + '.' + this.frameExt + '?v=' + this.assetsVersion + '&width=' + this._frameWidth();
    }

    loadFrame(i, priorite) {
      if (i < 0 || i >= this.frameCount) return null;
      var img = this.frames[i];
      if (img) return img;
      img = new Image();
      if (priorite) img.fetchPriority = priorite;
      img.decoding = 'async';
      img.src = this.frameUrl(i);
      this.frames[i] = img;
      return img;
    }

    /* Le warm (~210 frames, 2,5-6 Mo mobile) ne démarre plus à DOMContentLoaded :
       il entrait en compétition réseau directe avec le média LCP du hero
       (mesuré : LCP home mobile > 20 s, Lighthouse 2026-07-10). Il attend que
       la section APPROCHE du viewport (IO, marge 1 écran) puis un créneau
       idle. La frame 0 est chargée dès l'armement (~30 Ko) pour un premier
       draw immédiat ; le poster couvre le reste du temps. Fluidité du scrub
       et qualité inchangées : mêmes frames, chargées au bon moment. */
    _armWarm() {
      var self = this;
      var start = function () {
        if (self._warmed) return;
        self._warmed = true;
        var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 200); };
        idle(function () { self._warm(); }, { timeout: 2000 });
      };
      this.loadFrame(0);
      if (!('IntersectionObserver' in window)) { setTimeout(start, 3000); return; }
      var io = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) { io.disconnect(); start(); return; }
        }
      }, { rootMargin: '100% 0px' });
      io.observe(this.section);
    }

    /* Par petits groupes (4 images en vol au plus) et en priorité basse : les centaines de requêtes d'un coup
       faisaient attendre les images des sections suivantes (« D'autres façons de jouer » grise jusqu'à 15 s en
       descendant vite, mesuré le 03/11). Les images demandées par le défilement (loadFrame sans priorité) passent
       devant. */
    _warm() {
      var self = this, step = this.isDesktop ? 1 : this.mobileStride, i = 0, enVol = 0, MAX = 4;
      (function suivant() {
        while (enVol < MAX && i < self.frameCount) {
          var deja = self.frames[i];
          var img = self.loadFrame(i, 'low');
          i += step;
          if (deja || !img || img.complete) continue;
          enVol++;
          var fin = function () { enVol--; suivant(); };
          img.addEventListener('load', fin, { once: true });
          img.addEventListener('error', fin, { once: true });
        }
      })();
    }

    /* Mode vidéo mobile : `autoplay muted playsinline` + `<source>` EN DUR dans le HTML, la recette
       d'autoplay du hero (qui marche sur iPhone). Lecture UNE fois (loop=false → elle se fige sur la
       dernière frame). ⚠️ Jamais de `pause()` au JS : un `pause()` hors-écran DÉSACTIVE l'autoplay iOS
       et la vidéo ne redémarre plus (c'était la cause du « ne démarre pas »). Le JS règle la vitesse,
       tient la porte d'arrivée ci-dessous et, si iOS bloque l'autoplay, propose le bouton de secours. */
    _initVideoMode() {
      var self = this, v = this.video;
      var setRate = function () { try { v.playbackRate = self.videoRate; } catch (e) {} };
      setRate();
      v.addEventListener('loadedmetadata', setRate);
      v.addEventListener('play', setRate);   // iOS peut remettre la vitesse à 1 au (re)démarrage

      /* Départ À L'ARRIVÉE (réglage « Départ de la vidéo mobile » = in_view, défaut) : sans porte, la
         lecture automatique démarrait au chargement, hors écran, et la vidéo était finie à l'arrivée
         (mesuré sous WebKit : 10,6 s sur 14 à l'entrée). La vidéo reste masquée derrière son 1er
         écran exact (iOS ne lance pas une vidéo invisible) jusqu'à être à 60 % à l'écran ; si elle a
         quand même avancé (Chrome Android), un seul retour au début, puis lecture. Jamais de pause(). */
      this.vposter = this.querySelector('[data-osyn-stage-vposter]');
      this.playBtn = this.querySelector('[data-osyn-stage-play]');
      /* Chrome lance la vidéo muette même masquée : tant que la porte est fermée, le calque reste */
      v.addEventListener('playing', function () {
        if (!self.section.classList.contains('is-gated')) self._markPlaying();
      });
      if (this.playBtn) {
        this.playBtn.addEventListener('click', function () {
          var pr; try { pr = v.play(); } catch (e) {}
          if (pr && pr.catch) pr.catch(function () {});
        });
      }
      var media = v.parentElement;
      if (this.dataset.videoTrigger !== 'native' && 'IntersectionObserver' in window && media) {
        this.section.classList.add('is-gated');
        /* 60 % de la part visible possible : un média plus haut que l'écran (téléphone en paysage)
           n'atteint jamais 60 % de sa propre hauteur, d'où la comparaison à min(média, écran) */
        this._videoIO = new IntersectionObserver(function (entries) {
          var e = entries[entries.length - 1];
          if (!e || !e.isIntersecting || !self._videoIO) return;
          var room = Math.min(e.boundingClientRect.height, e.rootBounds ? e.rootBounds.height : innerHeight);
          if (e.intersectionRect.height < 0.6 * room) return;
          self._videoIO.disconnect(); self._videoIO = null;
          self._startVideo();
        }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
        this._videoIO.observe(media);
      }

      if (this._introHidden()) return;   // intro masquée sur mobile : aucun écouteur de sortie
      // intro : sortie liée au scroll + géométrie recalculée au resize (rotation téléphone in-range)
      this._onIntroScroll = function () {
        if (self._introRaf) return;
        self._introRaf = requestAnimationFrame(function () { self._introRaf = null; self._updateIntro(); });
      };
      this._onIntroResize = function () {
        self._measureHeader();
        if (self.introInner) self._paneH = self.introInner.getBoundingClientRect().height;
        self._updateIntro();
      };
      addEventListener('scroll', this._onIntroScroll, { passive: true });
      addEventListener('resize', this._onIntroResize);
      this._updateIntro();
    }

    _introHidden() { return !!(this.introEl && this.introEl.dataset.introMobile === 'off'); }

    _startVideo() {
      var self = this, v = this.video, started = false;
      var go = function () {
        if (started) return;
        started = true;
        self.section.classList.remove('is-gated');
        if (!v.paused) self._markPlaying();   // déjà lancée masquée (Chrome) : pas d'autre « playing »
        var pr; try { pr = v.play(); } catch (e) {}
        if (pr && pr.catch) pr.catch(function () { self._showPlay(); });
        /* filet : lecture toujours à l'arrêt 1,5 s après l'arrivée → bouton de secours */
        clearTimeout(self._watch);
        self._watch = setTimeout(function () { if (v.paused && v.currentTime < 0.05) self._showPlay(); }, 1500);
      };
      if (v.currentTime > 0.05) {
        v.addEventListener('seeked', go, { once: true });
        setTimeout(go, 400);
        try { v.currentTime = 0; } catch (e) { go(); }
      } else {
        go();
      }
    }

    _markPlaying() {
      this.section.classList.add('is-playing');
      if (this.playBtn) this.playBtn.hidden = true;
    }

    _showPlay() {
      if (this.playBtn && !this.section.classList.contains('is-playing')) this.playBtn.hidden = false;
    }

    _sizeCanvas() {
      var dpr = Math.min(window.devicePixelRatio || 1, 3);
      var rect = this.canvas.getBoundingClientRect();
      this.dispW = Math.max(1, Math.round(rect.width));
      this.dispH = Math.max(1, Math.round(rect.height));
      this.canvas.width = Math.round(this.dispW * dpr);
      this.canvas.height = Math.round(this.dispH * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';
      this._draw(this.drawnIdx >= 0 ? this.drawnIdx : 0, true);
    }

    _measureHeader() {
      var header = document.querySelector('.shopify-section-group-header-group .header, .header');
      var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
      this.section.style.setProperty('--osyn-stage-top', h + 'px');
    }

    _layoutCallouts() {
      if (!this.calloutsWrap || !this.view) return;
      // Les callouts vivent DANS la vue collante (les 2 modes) : bande basse en mobile (CSS grid-row 2),
      // absolus autour de la manette en desktop. Plus de reparent ni de classe `is-flow`.
      if (this.calloutsWrap.parentElement !== this.view) this.view.appendChild(this.calloutsWrap);
      this.calloutsWrap.classList.remove('is-flow');    // nettoie un éventuel état d'une session précédente
      this._flow = false;                                // reveal piloté par la progression dans les 2 modes
      this._setupReveal();
    }

    // Reveal Apple (fondu+glissé) staggered, piloté par la progression du scrub (_tick), in-view dans
    // les 2 modes. Pas d'IntersectionObserver : les callouts sont déjà dans la vue collante.
    _setupReveal() {
      if (!this.callouts || !this.callouts.length) return;
      this.callouts.forEach(function (c, i) {
        c.style.setProperty('--reveal-delay', (i * 90) + 'ms');   // stagger Apple
      });
      if (this._revealIO) { this._revealIO.disconnect(); this._revealIO = null; }
    }

    // Intro : reveal d'entrée (fondu+montée par ligne) quand le titre entre dans le viewport.
    // Gate `.is-ready` posée par JS → sans JS le titre reste visible. One-shot (agit sur la 1ʳᵉ callback,
    // donc gère le cas « déjà dans le viewport » : éditeur / page courte).
    _setupIntroReveal() {
      var content = this.introEl && this.introEl.querySelector('.osyn-home-stage__intro-content');
      if (!content) return;
      this.introEl.classList.add('is-ready');
      var introEl = this.introEl;
      var io = this._introIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { introEl.classList.add('is-in'); io.disconnect(); }
        });
      }, { root: null, rootMargin: '0px 0px -35% 0px', threshold: 0.01 });
      io.observe(content);
    }

    // Intro : sortie liée au scroll. --intro-p reste 0 tant que le titre est tenu (le bas de piste couvre
    // le pane), puis rampe 0→1 quand la piste se retire et que la manette entre. Basé sur la hauteur RÉELLE
    // du pane (résout svh-vs-innerHeight + offset header).
    _updateIntro() {
      if (!this.introEl) return;
      var r = this.introEl.getBoundingClientRect();
      var paneH = this._paneH || window.innerHeight;
      var F = paneH * 0.55;                                 // bande de fondu
      var ip = clamp((paneH - r.bottom) / F, 0, 1);
      if (ip !== this._introP) { this.introEl.style.setProperty('--intro-p', ip.toFixed(3)); this._introP = ip; }
    }

    _onResize() {
      this.isDesktop = matchMedia(MQ_DESKTOP).matches;
      this._measureHeader();
      if (this.introInner) this._paneH = this.introInner.getBoundingClientRect().height;
      this._layoutCallouts();
      this._sizeCanvas();
      this._onScroll();
    }

    _onScroll() {
      var rect = this.getBoundingClientRect();           // <osyn-home-stage> = la piste
      var scrollH = rect.height - window.innerHeight;
      this.target = clamp(scrollH > 0 ? -rect.top / scrollH : 0, 0, 1);
      if (this.raf == null) this.raf = requestAnimationFrame(this._tick);
    }

    _tick() {
      var now = performance.now();
      var dt = Math.min(50, now - (this._lastTs || now));
      this._lastTs = now;
      var factor = 1 - Math.exp(-dt / this.smoothingMs);
      this.p += (this.target - this.p) * factor;
      if (Math.abs(this.target - this.p) < 0.0004) this.p = this.target;

      // l'animation gèle à endProgress ; le reste du scroll (hold) laisse les callouts remplir le blanc.
      // Mobile : endP hardcodé 0.92 (la manette finit à 0.92, hold court 0.92→1.0 pendant le reveal) —
      // PAS le réglage global 0.85 qui piloterait aussi le desktop.
      this._draw(this._frameIdx(this.p), false);

      if (this.railThumb && this.railThumb.parentElement) {
        var max = this.railThumb.parentElement.clientHeight - this.railThumb.offsetHeight;
        this.railThumb.style.transform = 'translate3d(0,' + (this.p * Math.max(0, max)).toFixed(2) + 'px,0)';
      }
      var nSteps = this.railSteps.length || this.callouts.length || 1;
      var chapter = Math.min(nSteps - 1, Math.floor(this.p * nSteps));
      this.railSteps.forEach(function (r, k) { r.classList.toggle('is-on', k === chapter); });
      // desktop : reveal séquentiel piloté par la progression (fenêtres data-in/out par callout).
      // mobile : les 3 callouts (bande basse) se révèlent ENSEMBLE à la fin de l'anim (p≥0.82), une
      // seule fois (latch one-way : pas de toggle → pas de clignotement au scroll-up).
      var p = this.p;
      if (this.isDesktop) {
        this.callouts.forEach(function (c) {
          var inAt = parseFloat(c.dataset.in) || 0;
          var outAt = parseFloat(c.dataset.out); if (isNaN(outAt)) outAt = 1;
          c.classList.toggle('is-visible', p >= inAt && p < outAt);
        });
      } else if (p >= 0.82 && !this._revealed) {
        this._revealed = true;
        this.callouts.forEach(function (c) { c.classList.add('is-visible'); });
      }

      this._updateIntro();   // sortie de l'intro (titre) liée au scroll — inconditionnel, avant le bail

      if (this.p !== this.target) this.raf = requestAnimationFrame(this._tick);
      else this.raf = null;
    }

    /* Image de l'animation pour une progression du défilement (0 à 1). */
    _frameIdx(prog) {
      var endP = this.isDesktop ? this.endProgress : 0.92;
      var animP = endP < 1 ? clamp(prog / endP, 0, 1) : prog;
      return Math.round(animP * (this.frameCount - 1));
    }

    _draw(idx, force) {
      if (!force && idx === this.drawnIdx) return;
      /* En défilant vite, les images traversées ne sont plus demandées une à une (des centaines de requêtes qui
         faisaient attendre les sections suivantes) : seulement la destination et ses voisines ; les autres arrivent
         par le préchargement en priorité basse (_warm). */
      var img = this.frames[idx];
      if (!img && Math.abs(idx - this._frameIdx(this.target)) <= AHEAD) img = this.loadFrame(idx);
      if (!img || !img.complete || !img.naturalWidth) {
        if (img && !img._bound) {
          img._bound = true; var self = this;
          img.addEventListener('load', function () { self._draw(idx, true); }, { once: true });
        }
        if (this.drawnIdx < 0) { this.ctx.fillStyle = this.bg; this.ctx.fillRect(0, 0, this.dispW, this.dispH); }
        return;
      }
      this.ctx.fillStyle = this.bg;
      this.ctx.fillRect(0, 0, this.dispW, this.dispH);
      this.ctx.drawImage(img, 0, 0, this.dispW, this.dispH);
      this.drawnIdx = idx;
      var dir = this.target >= this.p ? 1 : -1;
      for (var k = 1; k <= AHEAD; k++) this.loadFrame(idx + dir * k);
      if (this.poster && !this._posterHidden) { this.poster.style.opacity = '0'; this._posterHidden = true; }
    }
  }

  if (window.customElements) customElements.define('osyn-home-stage', OsynHomeStage);

  // Réinitialisation dans l'éditeur de thème (au cas où l'élément est ré-attaché sans recréation)
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector && e.target.querySelector('osyn-home-stage');
    if (el && el.isConnected && !el._init && el.connectedCallback) el.connectedCallback();
  });
})();
